import { useState, useEffect } from 'react';
import { formatTimeRemaining } from '../../lib/dateFormat';
import RollingNumber from './RollingNumber';
import { useCachedImage } from '../../hooks/useCachedImage';
import { useTenant } from '@/contexts/TenantContext';
import AdSlot from '../ads/AdSlot';
import { generateAdDivId } from '@/config/ads.config';
import { useOverLogoPlacement } from '@/hooks/useOverLogoPlacement';
import { isNbaSquaresSport, squaresTeamAssetPadding } from '@/utils/squaresHeaderAssets';
import { MATCHUP_BAND_PX } from '@/utils/desktopColumn';

export interface TeamInfo {
  /** Team name (e.g., "Duke", "Arizona") */
  name: string;
  /** Team abbreviation for jersey (e.g., "DUKE", "ARIZ") */
  abbreviation: string;
  /** Team primary color (hex) */
  primaryColor: string;
  /** Team secondary color (hex) */
  secondaryColor?: string;
  /** Jersey image URL (optional - from asset service) */
  jerseyUrl?: string;
}

export interface GameHeaderProps {
  /** Team 1 info (left side) */
  team1: TeamInfo;
  /** Team 2 info (right side) */
  team2: TeamInfo;
  /** Contest sponsor logo URL */
  sponsorLogoUrl?: string;
  /** Contest id — drives `pid`/`s5` key-value targeting on the over-logo GAM slot */
  contestId?: string | null;
  /** Contest name/title */
  contestName?: string;
  /** Prize pool display value (e.g., "$40,000") */
  prizePool: string;
  /** Header background artwork for desktop, and for every device when no mobile asset is set */
  backgroundImageUrl?: string;
  /** Optional narrow-screen crop of the header artwork; falls back to the desktop one */
  backgroundImageMobileUrl?: string;
  /** Number of squares user can select */
  selectableSquares?: number;
  /** Info button click handler (right side - opens contest rules/details popup) */
  onInfoClick?: () => void;
  /** Is this results mode? */
  isResultsMode?: boolean;
  /** Selected square ID (for results mode) */
  selectedSquare?: number;
  /** Row numbers (for calculating selected square position) */
  rowNumbers?: number[];
  /** Column numbers (for calculating selected square position) */
  colNumbers?: number[];
  /** Team 1 current score (for results mode) */
  team1Score?: number;
  /** Team 2 current score (for results mode) */
  team2Score?: number;
  /** Start time UTC (for countdown display before game starts) */
  startTimeUtc?: string;
  /** Is game active/live/completed? */
  isGameLive?: boolean;
  /** Live fixture data (scores, quarter, clock) */
  liveFixtureData?: {
    team1Score?: number | null;
    team2Score?: number | null;
    quarter?: string;
    clock?: string;
    status?: 'scheduled' | 'live' | 'finished' | 'postponed' | 'cancelled';
  };
  /** Contest sport (e.g. NCAAMB, NBA) — used to normalize NBA jersey/badge framing vs NCAA */
  sport?: string;
}

/**
 * Live scoreboard type size, keyed to the header's own width.
 *
 * Was `clamp(32px, 10vw, 48px)`: inside the capped column `10vw` is measured
 * against the browser window, so on any desktop it pinned straight to the 48px
 * ceiling no matter how narrow the column actually was. `10cqw` is the same 10%,
 * measured against the header container instead — identical on mobile (where the
 * column is the viewport) and honest everywhere else.
 */
const SCORE_FONT_SIZE = 'clamp(32px, 10cqw, 48px)';

/**
 * Hero geometry, expressed as custom properties so the jerseys, the centre block's
 * side reservation and the band all derive from one set of numbers.
 *
 * `--sq-gutter`  distance from the column edge to the matchup band. Zero until the
 *                column outgrows the band, so every phone width is unchanged.
 * `--sq-jersey-w` jersey box width, tracking the column rather than sitting at a flat
 *                195px. At 195px on a 360px phone the two boxes covered 95% of the
 *                width and each overlapped the 208px centre logo by ~95px, which is
 *                why they read oversized; 46cqw is ~166px there, clearing the logo.
 *                The 225px ceiling is set by height, not taste: `aspect-ratio: 124/138`
 *                at 225px is 250px tall, which still clears the top of the 300px
 *                desktop band once the jersey is sat 28px up from the header bottom.
 *                The 160px floor keeps a very narrow frame from shrinking them away.
 * `--sq-jersey-bleed` how far the jersey hangs outside the band. On a phone the band
 *                edge *is* the screen edge, so this is the original crop-off-the-edge
 *                effect; inside a framed desktop column it lands harmlessly in the
 *                gutter and nothing is clipped.
 */
const HERO_VARS: Record<string, string> = {
  '--sq-gutter': `max(0px, (100cqw - ${MATCHUP_BAND_PX}px) / 2)`,
  '--sq-jersey-w': 'clamp(160px, 46cqw, 225px)',
  '--sq-jersey-bleed': '24px',
};

/**
 * Hook to get live countdown time for a contest
 * Updates every second for contests less than 24 hours away
 */
const useLiveCountdown = (startTimeUtc?: string): string => {
  const [timeDisplay, setTimeDisplay] = useState<string>('TBD');

  useEffect(() => {
    if (!startTimeUtc) {
      setTimeDisplay('TBD');
      return;
    }

    const startDate = new Date(startTimeUtc);
    const now = new Date();
    const diffMs = startDate.getTime() - now.getTime();

    // Only set up interval if contest is less than 24 hours away and hasn't started
    if (diffMs > 0 && diffMs < 24 * 60 * 60 * 1000) {
      // Initial calculation
      setTimeDisplay(formatTimeRemaining(startDate));

      // Update every second
      const interval = setInterval(() => {
        const currentTime = new Date();
        const currentDiff = startDate.getTime() - currentTime.getTime();

        if (currentDiff <= 0) {
          // Contest has started
          setTimeDisplay(formatTimeRemaining(startDate));
          clearInterval(interval);
        } else {
          setTimeDisplay(formatTimeRemaining(startDate));
        }
      }, 1000);

      return () => clearInterval(interval);
    } else {
      // For contests >= 24 hours or already started, just calculate once
      setTimeDisplay(formatTimeRemaining(startDate));
    }
  }, [startTimeUtc]);

  return timeDisplay;
};

/**
 * Game Header Component
 * Displays the top banner with team jerseys, sponsor logo, and prize pool
 */
export default function GameHeader({
  team1,
  team2,
  sponsorLogoUrl,
  contestName: _contestName = 'SQUARES',
  prizePool,
  backgroundImageUrl,
  backgroundImageMobileUrl,
  selectableSquares = 1,
  onInfoClick,
  isResultsMode = false,
  selectedSquare,
  rowNumbers,
  colNumbers,
  team1Score,
  team2Score,
  startTimeUtc,
  isGameLive = false,
  liveFixtureData,
  sport,
  contestId,
}: GameHeaderProps) {
  // Get live countdown for start time
  const liveStartTime = useLiveCountdown(startTimeUtc);
  const { isAdsEnabled, adPlacements, isPlacementEnabled } = useTenant();

  const isLogoAdEnabled =
    isAdsEnabled && adPlacements?.SQUARES_OVER_LOGO && isPlacementEnabled('SQUARES_OVER_LOGO');
  const overLogoPlacement = useOverLogoPlacement(adPlacements?.SQUARES_OVER_LOGO, contestId);

  // Check if game has started (only show countdown before game starts)
  const startTime = startTimeUtc ? new Date(startTimeUtc) : null;
  const now = new Date();
  const isGameStarted = startTime && now >= startTime;
  const shouldShowStartTime = !isResultsMode && startTimeUtc && !isGameStarted;

  // Calculate selected square position in results mode or live games
  let selectedRowNum: number | undefined;
  let selectedColNum: number | undefined;
  if ((isResultsMode || isGameLive) && selectedSquare !== undefined && rowNumbers && colNumbers) {
    const row = Math.floor((selectedSquare - 1) / 10); // Convert square_number (1-100) to row index (0-9)
    const col = (selectedSquare - 1) % 10; // Convert square_number (1-100) to col index (0-9)
    selectedRowNum = rowNumbers[row];
    selectedColNum = colNumbers[col];
  }

  const nbaHeader = isNbaSquaresSport(sport);

  // The desktop asset doubles as the default, so it is what the bundled artwork backstops.
  const desktopBackgroundImage = backgroundImageUrl?.trim() || '/Play-background.svg';
  const mobileBackgroundImage = backgroundImageMobileUrl?.trim() || null;

  return (
    /**
     * Size container for the header.
     *
     * The header lives inside the capped desktop content column, but everything
     * inside it used to size itself off the *viewport* (`sm:` padding, `vw` type).
     * Those two never agree once the column stops being the viewport, which is
     * exactly what happens on desktop. Declaring a size container here lets the
     * children measure the column they are actually in, via `cqw`.
     */
    <div style={{ containerType: 'inline-size' }}>
      {/*
       * Everything below measures the column via `cqw`, so it has to sit *inside* the
       * size container rather than on it — an element cannot container-query itself.
       * This is also the positioning context the jerseys hang off.
       */}
      <div className="relative" style={{ ...HERO_VARS }}>
        {/* Background Image / Ad Slot — full-bleed across the column, never banded */}
        <div
          className="relative w-full overflow-hidden min-h-[220px] md:min-h-[300px]"
          style={{ backgroundColor: '#1a1a2e' }}
        >
          {/*
           * Two artwork assets, a wide desktop letterbox (994×163) and a squarer phone panel
           * (430×187), picked by CSS at the same `md` breakpoint the rest of this header
           * switches on. The choice is a media query rather than a JS `isMobile` hook because
           * a hook can only report after mount, which would flash the wrong crop on first
           * paint. When no mobile asset is configured only the one layer is rendered, so the
           * desktop asset covers every device exactly as it does today.
           */}
          {mobileBackgroundImage ? (
            <>
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-cover bg-center md:hidden"
                style={{ backgroundImage: `url(${mobileBackgroundImage})` }}
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 hidden bg-cover bg-center md:block"
                style={{ backgroundImage: `url(${desktopBackgroundImage})` }}
              />
            </>
          ) : (
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${desktopBackgroundImage})` }}
            />
          )}

          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />

          {/* Content — capped to the matchup band and centred, so its side reservation
            lines up with where the jerseys actually are */}
          <div
            className="relative z-10 flex items-center justify-center mx-auto min-h-[220px] md:min-h-[300px]"
            style={{
              maxWidth: `${MATCHUP_BAND_PX}px`,
              /*
               * Clear the two jerseys, which are anchored to the band edges and hang
               * `--sq-jersey-bleed` outside them — so the part that actually intrudes on
               * the centre block is `--sq-jersey-w` minus that bleed.
               *
               * That reservation is a property of the column, not of the device, so it
               * cannot be a `sm:` breakpoint: inside a 500px column `sm:px-44` fired off
               * the 1440px desktop viewport and left 148px for a 208px logo.
               *
               * 34cqw reproduces the old mobile 128px at a 375px column; the jersey-width
               * term caps what a wide column reserves at what the jerseys actually use.
               */
              paddingInline: 'min(calc(var(--sq-jersey-w) - var(--sq-jersey-bleed)), 34cqw)',
              paddingTop: '16px',
              paddingBottom: '20px',
            }}
          >
            {/* Center: Logo + Prize */}
            <div className="flex flex-col items-center text-center w-full">
              {/* Center logo: NCAAMB/NFL baseline; NBA badge assets often read larger at same box size */}
              <div
                className={`relative mb-2 flex items-center justify-center flex-shrink-0 ${nbaHeader ? 'h-20 w-48 md:h-36 md:w-72' : 'h-24 w-52 md:h-40 md:w-72'}`}
              >
                {/* Over Logo Ad Slot (1x1 tracking pixel, positioned just above logo) */}
                {isLogoAdEnabled && overLogoPlacement && (
                  <div className="absolute bottom-full w-[1px] h-[1px] overflow-hidden pointer-events-none">
                    <AdSlot
                      divId={generateAdDivId(adPlacements, 'SQUARES_OVER_LOGO', true, 0)}
                      adPlacement={overLogoPlacement}
                      placement="squares-over-logo"
                    />
                  </div>
                )}
                {sponsorLogoUrl && sponsorLogoUrl.trim().length > 0 ? (
                  <img
                    src={sponsorLogoUrl}
                    alt="Contest Logo"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <img
                    src="/DFS_NFL_Wrangler_Squares_Logo_1080x1080%201.svg"
                    alt="Wrangler Squares Logo"
                    className="h-full w-full object-contain"
                  />
                )}
              </div>

              {/* Prize Info or Selected Square Info */}
              {(isResultsMode || isGameLive) &&
              selectedRowNum !== undefined &&
              selectedColNum !== undefined ? (
                <div className="text-center px-4 w-full flex-shrink-0 mt-1">
                  {/* "Your square:" label */}
                  <p
                    className="mb-1"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: '#FFFFFF',
                    }}
                  >
                    Your square:
                  </p>

                  {/*
                   * Team names with the square numbers in the centre. Same three-track
                   * construction as the score row below, and for the same reason: this used
                   * to be `max-content 3.5ch max-content` centred as a group, which only puts
                   * the MIDDLE cell on the container's axis when the two abbreviations happen
                   * to be the same width. They rarely are, so `9-3` sat ~3.3px off the axis
                   * that the colon, `Final` and `Your square:` all share.
                   *
                   * Equal outer tracks fix that, but they also expose the abbreviations to
                   * exactly the overflow the score row below has to deal with: `UCONN` is
                   * 35.5px wide in a ~19px track at a 393px column. The `columnGap` does not
                   * save it — 10px of gap against a ~16px overflow — and a `text-align`ed
                   * block spends the gap and then eats into the middle cell (measured 4.9px
                   * inside it at 360px). So the two outer cells take the same flex treatment
                   * as the scores and overflow outwards past the jersey padding instead,
                   * which leaves the 10px gap intact at every width.
                   *
                   * `whitespace-nowrap` is load-bearing, not tidy-up. As a flex container the
                   * `<p>`'s text becomes an ANONYMOUS flex item whose automatic minimum is its
                   * min-content width — the longest WORD, not the whole string — where the old
                   * `max-content` track could never wrap. `getTeamCode`'s fallback is
                   * `raw.substring(0, 4)`, so an unmapped "UC Irvine" yields "UC I": a soft-wrap
                   * opportunity that would break onto two lines in a ~19px track and double the
                   * row's height. Pinning nowrap makes min-content the full run again.
                   */}
                  <div
                    className="grid items-center mb-1"
                    style={{
                      gridTemplateColumns: 'minmax(0, 1fr) 3.5ch minmax(0, 1fr)',
                      columnGap: '10px',
                    }}
                  >
                    <p
                      className="text-white flex justify-end whitespace-nowrap"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 800,
                        fontSize: '16.92px',
                        lineHeight: '20px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {team1.abbreviation}
                    </p>
                    <p
                      className="text-white text-center"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 800,
                        fontSize: '23.82px',
                        lineHeight: '28px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {selectedRowNum}-{selectedColNum}
                    </p>
                    <p
                      className="text-white flex justify-start whitespace-nowrap"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 800,
                        fontSize: '16.92px',
                        lineHeight: '20px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {team2.abbreviation}
                    </p>
                  </div>

                  {/* Live scores: status above, scoreboard-style score below */}
                  {liveFixtureData &&
                  liveFixtureData.team1Score !== null &&
                  liveFixtureData.team1Score !== undefined &&
                  liveFixtureData.team2Score !== null &&
                  liveFixtureData.team2Score !== undefined ? (
                    <>
                      {/* Status (Final, clock) — NCAA maps Q1/Q2→H1/H2; NBA keeps Q1–Q4 */}
                      {(liveFixtureData.quarter ||
                        liveFixtureData.clock ||
                        liveFixtureData.status === 'live') && (
                        <p
                          className="mb-0.5"
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '16px',
                            color: '#FFFFFF',
                          }}
                        >
                          {(() => {
                            const isLive = liveFixtureData.status === 'live';
                            const isFinished =
                              liveFixtureData.status === 'finished' ||
                              (liveFixtureData.quarter === 'FT' &&
                                (!liveFixtureData.clock ||
                                  /^0*:?0*$/.test(
                                    String(liveFixtureData.clock || '').replace(/\s/g, '')
                                  )));
                            if (isFinished) return 'Final';
                            const q = liveFixtureData.quarter;
                            const displayQuarter =
                              nbaHeader || !q ? q : q === 'Q1' ? 'H1' : q === 'Q2' ? 'H2' : q;
                            const clockPart = liveFixtureData.clock
                              ? ` ${liveFixtureData.clock}`
                              : '';
                            if (isLive && (displayQuarter || liveFixtureData.clock))
                              return `${displayQuarter || ''}${clockPart}`.trim() || 'Live';
                            return displayQuarter ? `${displayQuarter}${clockPart}` : 'Live';
                          })()}
                        </p>
                      )}
                      {/*
                       * Scoreboard-style: the colon pinned to the container's exact centre,
                       * with each digit run overflowing away from it.
                       *
                       * 1. Why the row is a grid of two equal outer tracks.
                       *
                       * It used to be `min-w-[140px]` over `max-content 3.5ch max-content`.
                       * Inside this centre block the content box is only ~94px at a 393px
                       * column — the `paddingInline` above reserves 133.6px each side for the
                       * jerseys and the wrapper's `px-4` another 32 — and a block-level box
                       * whose min-width exceeds its containing
                       * block does NOT re-centre: its margins stay 0, so it laid out from the
                       * content-box left edge and overflowed the whole ~46px difference to the
                       * right. The shift was (140 - contentWidth) / 2, so ~23px at 393px,
                       * falling to zero at a column of 537.5px or wider — which is why it read
                       * as a phone-only defect. Two equal outer tracks put the colon on the
                       * container's centre by construction, on the same axis as `Final` and
                       * `Your square:` above it. Desktop is unchanged wherever the two scores
                       * are the same width; where they differ, the colon now sits on the axis
                       * instead of the group being centred on it. That delta is half a digit's
                       * width, so it scales with the font: ~7.4px at a 393px column, ~9.1px
                       * once `clamp(32px, 10cqw, 48px)` is at its 48px cap (measured 9.11px at
                       * 1440). Same fix, not a regression — but not a no-op either, and live
                       * games hit the unequal case constantly.
                       *
                       * 2. Why the tracks are `minmax(0, 1fr)` and NOT plain `1fr`.
                       *
                       * `1fr` means `minmax(auto, 1fr)`, and `auto` floors each track at its
                       * item's min-content width. RollingNumber is an inline-flex that cannot
                       * wrap, so that floor is the full digit run — at `100 : 98` in a ~94px
                       * container the free space goes negative, both tracks fall back to their
                       * own unequal minimums, and the colon lands 15.4px off-axis (30.3px at
                       * four digits). `minmax(0, ...)` removes that floor and keeps the two
                       * sides equal at every score.
                       *
                       * 3. Why the two score spans are flex containers and not `text-align`ed
                       *    blocks.
                       *
                       * Equal tracks mean a 3-digit run is wider than its track on every phone
                       * — 44.5px of digits in a 28.9px track at 390px — so it has to overflow
                       * somewhere, and the only tolerable direction is outwards, away from the
                       * colon.
                       *
                       * `text-align` cannot deliver that. It distributes only POSITIVE free
                       * space; once the run is wider than the line box the free space is
                       * negative, alignment stops applying, and the run lays out from the line
                       * box's start edge and overflows towards the end edge — i.e. rightwards,
                       * INTO the colon. That is harmless on the right-hand score, whose
                       * overflow already points away from the colon (which is why `9 : 103`
                       * always looked fine), and fatal on the left-hand one: `text-right`
                       * bought nothing and `112 : 108` rendered with the colon inside the `2`,
                       * 15.5px of digits sitting in the colon's cell.
                       *
                       * Flex alignment does act on negative free space. With a single flex
                       * item wider than its container, `justify-content` still parks the item
                       * against the edge it names and lets it overflow the opposite edge —
                       * the "unsafe" alignment behaviour that the `safe` keyword exists to opt
                       * out of. So `justify-end` on the left span puts the run's right edge on
                       * the track's right edge and spills the excess leftwards; `justify-start`
                       * on the right span mirrors it. Both runs grow outwards and the colon's
                       * cell is never encroached, at any score or width.
                       *
                       * ⚠️ Outwards is into the band the JERSEY is painted in, not empty space.
                       * The jersey wrappers are `absolute z-10` and the content block is
                       * `relative z-10`, so on equal z-index they win on tree order. On a phone
                       * a 3-digit left score spills far enough to sit under the left jersey.
                       * If that ever shows, raise the content block to `z-20` — the jerseys are
                       * decorative and already bleed off-canvas — rather than re-doing layout.
                       *
                       * The digits are not squeezed in the process, but the reason is NOT that
                       * RollingNumber can't wrap: each digit slot is `inline-block` with
                       * `overflow-hidden`, whose automatic minimum resolves to 0. What actually
                       * floors the run is the explicit `minWidth: digitMinWidth` on every slot,
                       * and a single-line flex container's min-content size is the sum of its
                       * items'. So the default `flex-shrink: 1` has nothing to take. Drop that
                       * `min-width` and the digits would shrink AND clip, since they already
                       * carry `overflow-hidden`. `align-items` is deliberately left alone here: the
                       * grid row's `items-center` already places the spans, and setting it on
                       * the spans measures identically.
                       */}
                      <div
                        className="grid items-center"
                        style={{
                          gridTemplateColumns: 'minmax(0, 1fr) 3.5ch minmax(0, 1fr)',
                        }}
                      >
                        <span
                          className="flex justify-end"
                          style={{
                            fontFamily: '"Saira Extra Condensed", sans-serif',
                            fontWeight: 800,
                            fontSize: SCORE_FONT_SIZE,
                            lineHeight: 1,
                            letterSpacing: '-0.04em',
                            color: 'white',
                          }}
                        >
                          <RollingNumber
                            value={liveFixtureData.team1Score}
                            digitMinWidth="0.38em"
                          />
                        </span>
                        <span
                          className="text-center relative -top-1"
                          style={{
                            fontFamily: '"Saira Extra Condensed", sans-serif',
                            fontWeight: 800,
                            fontSize: SCORE_FONT_SIZE,
                            lineHeight: 1,
                            color: 'white',
                          }}
                        >
                          :
                        </span>
                        <span
                          className="flex justify-start"
                          style={{
                            fontFamily: '"Saira Extra Condensed", sans-serif',
                            fontWeight: 800,
                            fontSize: SCORE_FONT_SIZE,
                            lineHeight: 1,
                            letterSpacing: '-0.04em',
                            color: 'white',
                          }}
                        >
                          <RollingNumber
                            value={liveFixtureData.team2Score}
                            digitMinWidth="0.38em"
                          />
                        </span>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <>
                  <p
                    className="text-sm tracking-wide"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 800,
                      color: '#FFFFFF',
                    }}
                  >
                    WIN YOUR SHARE OF
                  </p>
                  <p
                    className="text-[#53d337] text-4xl"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 800,
                      letterSpacing: '0.5%',
                    }}
                  >
                    {prizePool}
                  </p>
                  {/* Start time countdown - only show before game starts */}
                  {shouldShowStartTime && (
                    <p
                      className="text-xs font-medium mt-0 mb-2"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        color: '#FFFFFF',
                      }}
                    >
                      Start: {liveStartTime}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/*
         * Team jerseys: in the outer wrapper so they can extend down past the hero to the
         * selection bar line, where both sit on one shared baseline (`bottom: 28px`) and
         * rise out of the divider above the grid.
         *
         * Anchored to the matchup band, not the column: `--sq-gutter` is 0 on a phone, so
         * this is exactly the old `left: -24px`, and on desktop it walks the pair inwards
         * with the band instead of leaving them cropped in the corners.
         */}
        <div
          className="absolute z-10 pointer-events-none"
          style={{ bottom: '28px', left: 'calc(var(--sq-gutter) - var(--sq-jersey-bleed))' }}
        >
          <TeamJersey team={team1} side="left" sport={sport} />
        </div>
        <div
          className="absolute z-10 pointer-events-none"
          style={{ bottom: '28px', right: 'calc(var(--sq-gutter) - var(--sq-jersey-bleed))' }}
        >
          <TeamJersey team={team2} side="right" sport={sport} />
        </div>

        {/* Selection Instructions Row or Results Info */}
        <div className="flex items-center justify-between px-4 py-2 gap-2">
          {isResultsMode || isGameLive ? (
            <div className="flex items-center gap-2 text-white text-sm flex-1 justify-center">
              {/* Left Triangle */}
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid #FFFFFF',
                  filter: 'drop-shadow(0px 0px 6.3px #F5F5F5)',
                }}
              />
              <span className="text-white">Percentage of square selections by users</span>
              {/* Right Triangle */}
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid #FFFFFF',
                  filter: 'drop-shadow(0px 0px 6.3px #F5F5F5)',
                }}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 text-white text-sm flex-1 justify-center">
              {/* Left Triangle */}
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid #FFFFFF',
                  filter: 'drop-shadow(0px 0px 6.3px #F5F5F5)',
                }}
              />
              <span className="text-white">
                Select ({selectableSquares}) {selectableSquares === 1 ? 'one square' : 'squares'}{' '}
                below
              </span>
              {/* Right Triangle */}
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid #FFFFFF',
                  filter: 'drop-shadow(0px 0px 6.3px #F5F5F5)',
                }}
              />
            </div>
          )}
          {/* Right: Info icon (opens contest rules popup) */}
          <button
            onClick={onInfoClick}
            className="flex-shrink-0 hover:opacity-80 transition-opacity"
            style={{ width: '18px', height: '18px' }}
            aria-label="Contest information"
          >
            <img src="/circle-info-outline.svg" alt="Info" className="w-full h-full" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Team Jersey Component
 * Displays team jersey directly on hero background (no rectangle). Larger, bottom-aligned
 * so jerseys "come up from" the section divider line below. Uses useCachedImage for
 * persistent in-memory caching across mounts/tab switches.
 */
function TeamJersey({
  team,
  side,
  sport,
}: {
  team: TeamInfo;
  side: 'left' | 'right';
  /** Contest sport — NBA jerseys and CFB helmets get inner padding so they don't fill the box */
  sport?: string;
}) {
  const { loaded, errored } = useCachedImage(team.jerseyUrl);
  const innerPadding = squaresTeamAssetPadding(sport, team.jerseyUrl);

  return (
    <div
      className={`relative flex flex-col justify-end flex-shrink-0 ${side === 'left' ? 'items-start' : 'items-end'}`}
      style={{
        /* Falls back to the original fixed 195px if rendered outside the hero's vars */
        width: 'var(--sq-jersey-w, 195px)',
        minWidth: 'var(--sq-jersey-w, 195px)',
        maxWidth: 'var(--sq-jersey-w, 195px)',
        aspectRatio: '124 / 138',
        flexShrink: 0,
        marginLeft: side === 'left' ? 0 : undefined,
        marginRight: side === 'right' ? 0 : undefined,
      }}
    >
      {team.jerseyUrl && !errored ? (
        <>
          {!loaded && <div className="absolute inset-0 bg-zinc-800/30 rounded-lg animate-pulse" />}
          {innerPadding ? (
            <div
              className="absolute inset-0 box-border flex items-end justify-center"
              style={{ padding: innerPadding }}
            >
              <img
                src={team.jerseyUrl}
                alt={`${team.name} jersey`}
                className={`max-h-full max-w-full object-contain object-bottom ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}
              />
            </div>
          ) : (
            <img
              src={team.jerseyUrl}
              alt={`${team.name} jersey`}
              className={`w-full h-full object-contain object-bottom ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}
            />
          )}
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center min-w-0 min-h-0 rounded-lg bg-zinc-800/30">
          <span className="text-zinc-400 text-sm font-semibold text-center px-1 truncate">
            {team.abbreviation || team.name.substring(0, 3).toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );
}
